import { createReducer, on } from '@ngrx/store';
import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { IAppeal, IAppealFilter } from './appeal.interface';
import {
	IModerationCase,
	IModerationDecision,
} from '../+moderation-store/moderation.interface';
import { AppealActions } from './appeal.actions';
import { IResponseError } from '../../service/interface';
import { AppealStatus } from '../../../shared/enums';

export const appealFeatureKey = 'appeal';

export interface IState extends EntityState<IAppeal> {
	loading: boolean;
	error?: IResponseError;
	filter: IAppealFilter;
	selectedAppeal?: IAppeal;
	contestedDecision?: IModerationDecision;
	contestedCase?: IModerationCase;
}

export const appealAdapter: EntityAdapter<IAppeal> =
	createEntityAdapter<IAppeal>({
		selectId: (appeal: IAppeal) => appeal.id,
	});

export const initialState: IState = appealAdapter.getInitialState({
	loading: false,
	filter: {
		status: AppealStatus.PENDING,
		pagination: {
			count: 0,
			limit: 10,
			offset: 1,
		},
	},
});

export const reducer = createReducer(
	initialState,
	// ── API ──

	on(
		AppealActions.loadAppeals,
		(state): IState => ({
			...state,
			loading: true,
			error: undefined,
		}),
	),
	on(
		AppealActions.loadAppealsSuccess,
		(state, action): IState =>
			appealAdapter.setAll(action.appeals, {
				...state,
				filter: {
					...state.filter,
					pagination: {
						...state.filter.pagination,
						count: action.pagination.count,
					},
				},
				loading: false,
				error: undefined,
			}),
	),
	on(
		AppealActions.loadAppealsError,
		(state, action): IState => ({
			...state,
			loading: false,
			error: action.error,
		}),
	),
	on(
		AppealActions.loadAppeal,
		(state): IState => ({
			...state,
			loading: true,
			error: undefined,
			selectedAppeal: undefined,
			contestedDecision: undefined,
			contestedCase: undefined,
		}),
	),
	on(
		AppealActions.loadAppealSuccess,
		(state, action): IState => ({
			...state,
			loading: false,
			error: undefined,
			selectedAppeal: action.appeal,
		}),
	),
	on(
		AppealActions.loadAppealError,
		(state, action): IState => ({
			...state,
			loading: false,
			error: action.error,
		}),
	),
	on(
		AppealActions.loadContestedDecisionSuccess,
		(state, action): IState => ({
			...state,
			contestedDecision: action.decision,
		}),
	),
	on(
		AppealActions.loadContestedDecisionError,
		(state, action): IState => ({
			...state,
			error: action.error,
		}),
	),
	on(
		AppealActions.loadContestedCaseSuccess,
		(state, action): IState => ({
			...state,
			contestedCase: action.moderationCase,
		}),
	),
	on(
		AppealActions.loadContestedCaseError,
		(state, action): IState => ({
			...state,
			error: action.error,
		}),
	),
	on(
		AppealActions.settleAppeal,
		(state): IState => ({
			...state,
			loading: true,
			error: undefined,
		}),
	),
	on(
		AppealActions.settleAppealSuccess,
		(state, action): IState => ({
			...state,
			loading: false,
			error: undefined,
			selectedAppeal: action.appeal,
		}),
	),
	on(
		AppealActions.settleAppealError,
		(state, action): IState => ({
			...state,
			loading: false,
			error: action.error,
		}),
	),

	// ── UI ──

	on(
		AppealActions.setOffset,
		(state, action): IState => ({
			...state,
			loading: true,
			filter: {
				...state.filter,
				pagination: {
					...state.filter.pagination,
					offset: action.offset,
				},
			},
		}),
	),
	on(
		AppealActions.setLimit,
		(state, action): IState => ({
			...state,
			loading: true,
			error: undefined,
			filter: {
				...state.filter,
				pagination: {
					...state.filter.pagination,
					offset: 1,
					limit: action.limit,
				},
			},
		}),
	),
	on(
		AppealActions.setSortOrder,
		(state, action): IState => ({
			...state,
			filter: {
				...state.filter,
				sortBy: action.sortBy as IAppealFilter['sortBy'],
				orderBy: action.orderBy,
				pagination: {
					...state.filter.pagination,
					offset: 1,
				},
			},
		}),
	),
	on(
		AppealActions.clearSortOrder,
		(state): IState => ({
			...state,
			filter: {
				...state.filter,
				sortBy: undefined,
				orderBy: undefined,
				pagination: {
					...state.filter.pagination,
					offset: 1,
				},
			},
		}),
	),
	on(
		AppealActions.setFilter,
		(state, action): IState => ({
			...state,
			filter: {
				...state.filter,
				...action.data,
				pagination: {
					...state.filter.pagination,
					offset: 1,
				},
			},
		}),
	),
	on(
		AppealActions.clearAppealDetail,
		(state): IState => ({
			...state,
			selectedAppeal: undefined,
			contestedDecision: undefined,
			contestedCase: undefined,
		}),
	),
	on(
		AppealActions.clearAppealError,
		(state): IState => ({
			...state,
			error: undefined,
		}),
	),
);
